import { useState, useEffect } from "react";
import Sidebar from "../components/layout/Sidebar";
import Header from "../components/layout/Header";
import Dialog from "../components/dashboard/Dialog";
import { useToast } from "../components/providers/ToastProvider";
import { useSession } from "../context/AuthContext";
import { usePlans } from "../context/PlanContext";
import { useNavigate, Link, useParams } from "react-router-dom";
import api from "../services/api";

export default function AdminStudentDetail() {
  const { id } = useParams<{ id: string }>();
  const { status } = useSession();
  const { plans } = usePlans();
  const navigate = useNavigate();
  const { showToast } = useToast();
  
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isTierModalOpen, setIsTierModalOpen] = useState(false);
  const [selectedPlanIds, setSelectedPlanIds] = useState<number[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Extra Creations Modal State
  const [isExtraModalOpen, setIsExtraModalOpen] = useState(false);
  const [extraCreationsInput, setExtraCreationsInput] = useState<number>(0);
  const [isSubmittingExtra, setIsSubmittingExtra] = useState(false);
  
  // Submission View Modal
  const [selectedSubmission, setSelectedSubmission] = useState<any>(null);

  const fetchDetails = async () => {
    try {
      const res = await api.get(`/admin/students/${id}/details`);
      setData(res.data);
    } catch (err) {
      console.error("Failed to fetch student details:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (status === "unauthenticated") {
       navigate("/login");
    } else if (status === "authenticated" && id) {
       fetchDetails();
    }
  }, [status, id, navigate]);

  const handleTierUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await api.patch(`/admin/students/${id}/membership`, { plan_ids: selectedPlanIds });
      setIsTierModalOpen(false);
      showToast("Access Level Updated", `${data?.user?.name}'s clearance has been re-authorized.`, "success");
      fetchDetails();
    } catch (err) {
      console.error("Tier update failed", err);
      showToast("Update Failed", "Could not synchronize the new membership tier.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExtraCreationsUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmittingExtra(true);
    try {
      await api.patch(`/admin/students/${id}/extra-creations`, { extraCreations: extraCreationsInput });
      setIsExtraModalOpen(false);
      showToast("Bonus Quota Updated", `${data?.user?.name}'s bonus creation quota has been updated.`, "success");
      fetchDetails();
    } catch (err) {
      console.error("Extra creations update failed", err);
      showToast("System Error", "Communication failure during bonus update.", "error");
    } finally {
      setIsSubmittingExtra(false);
    }
  };

  const getTierDetails = (tier: string) => {
    switch (tier) {
      case "VIP": return { color: "bg-cyan-500 text-white", border: "border-cyan-500/20", icon: "auto_awesome" };
      case "PREMIUM": return { color: "bg-amber-500 text-white", border: "border-amber-500/20", icon: "workspace_premium" };
      case "GROWTH": return { color: "bg-slate-500 text-white", border: "border-slate-500/20", icon: "military_tech" };
      default: return { color: "bg-slate-500 text-white", border: "border-slate-500/20", icon: "person" };
    }
  };

  if (loading || !data) {
    return (
      <div className="flex h-screen bg-background text-on-background">
        <Sidebar />
        <div className="flex-1 flex flex-col ml-0 md:ml-20">
          <Header />
          <div className="flex-1 p-10 animate-pulse space-y-12 mt-16">
            <div className="h-64 bg-surface-container-low rounded-[3rem]" />
            <div className="grid grid-cols-3 gap-6">
              {[1, 2, 3].map(i => <div key={i} className="h-32 bg-surface-container-low rounded-[2rem]" />)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { user, activeTracks, submissions } = data;
  const tier = getTierDetails(user.highest_plan_name);

  return (
    <div className="min-h-screen bg-background text-on-background">
      <Header />
      <Sidebar />
      
      <main className="ml-0 md:ml-20 pt-24 pb-12 px-4 md:px-8">
        <div className="max-w-6xl mx-auto space-y-12">
          
          {/* Tactical Breadcrumb */}
          <nav className="flex items-center gap-2 px-2">
             <Link 
               to="/admin/students" 
               className="text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-primary transition-colors flex items-center gap-1"
             >
                <span className="material-symbols-outlined text-[14px]">arrow_back</span>
                Student Census
             </Link>
             <span className="text-slate-300">/</span>
             <span className="text-[10px] font-black uppercase tracking-widest text-primary truncate max-w-[200px]">
                {user.name} Dossier
             </span>
          </nav>
          <div className="relative overflow-hidden bg-surface-container-low rounded-[3.5rem] p-12 border border-outline-variant/10 group">
             <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
             
             <div className="relative flex flex-col md:flex-row items-center gap-10">
                <div className={`w-36 h-36 rounded-[2.5rem] flex items-center justify-center text-4xl font-black shadow-2xl ${tier.color}`}>
                   {user.name?.[0] || "S"}
                </div>
                
                <div className="flex-1 text-center md:text-left space-y-4">
                   <div>
                      <h1 className="text-4xl font-black tracking-tighter text-on-surface">{user.name}</h1>
                      <p className="text-slate-500 font-medium">{user.email}</p>
                   </div>
                   
                   <div className="flex flex-wrap items-center justify-center md:justify-start gap-4">
                      <div className={`px-6 py-2 rounded-full flex items-center gap-2 border ${tier.border} font-bold text-xs uppercase tracking-widest ${tier.color}`}>
                         <span className="material-symbols-outlined text-[14px]">{tier.icon}</span>
                         {user.highest_plan_name} RANK
                      </div>
                      <button 
                         onClick={() => {
                           setSelectedPlanIds(user.subscriptions?.map((s: any) => Number(s.plan_id)) || []);
                           setIsTierModalOpen(true);
                         }}
                         title="Upgrade Tier"
                         className="w-8 h-8 rounded-full flex items-center justify-center bg-primary text-surface hover:translate-y-[-2px] transition-transform shadow-md shadow-primary/20"
                      >
                         <span className="material-symbols-outlined text-[16px]">upgrade</span>
                      </button>

                      {/* Extra Creations Bonus Badge */}
                      <div className="px-6 py-2 rounded-full border border-outline-variant/10 bg-white/50 text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                         <span className="material-symbols-outlined text-[14px]">tune</span>
                         Bonus Quota: +{user.extraCreations || 0}
                      </div>
                      <button 
                         onClick={() => {
                           setExtraCreationsInput(user.extraCreations || 0);
                           setIsExtraModalOpen(true);
                         }}
                         title="Award Bonus Creations"
                         className="w-8 h-8 rounded-full flex items-center justify-center bg-indigo-500 text-white hover:translate-y-[-2px] transition-transform shadow-md shadow-indigo-500/20"
                      >
                         <span className="material-symbols-outlined text-[16px]">add_circle</span>
                      </button>

                      <div className="px-6 py-2 rounded-full border border-outline-variant/10 bg-white/50 text-[10px] font-black uppercase tracking-widest text-slate-500">
                         Joined {new Date(user.created_at || user.createdAt).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
                      </div>
                   </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                   <div className="bg-white p-6 rounded-[2rem] border border-outline-variant/10 text-center shadow-sm">
                      <span className="block text-2xl font-black text-primary">{user.metrics?.points || 0}</span>
                      <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Tactical Points</span>
                   </div>
                   <div className="bg-white p-6 rounded-[2rem] border border-outline-variant/10 text-center shadow-sm">
                      <span className="block text-2xl font-black text-indigo-500">{user.metrics?.xp || 0}</span>
                      <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Experience Points</span>
                   </div>
                </div>
             </div>
          </div>

          {/* Business Profile Summary */}
          {user.businessProfile && (
            <div className="bg-surface-container-low rounded-[3.5rem] p-10 border border-outline-variant/10">
               <div className="flex items-center gap-4 mb-8 px-2">
                  <div className="w-2 h-8 bg-emerald-500 rounded-full" />
                  <h2 className="text-2xl font-black tracking-tight text-on-surface">Business Genesis <span className="text-slate-400 font-medium">Profile</span></h2>
               </div>

               <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
                  <div className="space-y-1">
                      <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Product/Service</span>
                      <p className="font-bold text-on-surface">{user.businessProfile.product_service_name}</p>
                  </div>
                  <div className="space-y-1">
                      <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Category</span>
                      <p className="font-bold text-on-surface">{user.businessProfile.category}</p>
                  </div>
                  <div className="space-y-1">
                      <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Location</span>
                      <p className="font-bold text-on-surface">{user.businessProfile.location}</p>
                  </div>
                  <div className="space-y-1">
                      <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Price Range</span>
                      <p className="font-bold text-on-surface">{user.businessProfile.price_range}</p>
                  </div>
                  <div className="space-y-1">
                      <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Goal</span>
                      <p className="font-bold text-on-surface">{user.businessProfile.goal}</p>
                  </div>
                  <div className="space-y-1">
                      <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Whatsapp</span>
                      <p className="font-bold text-on-surface">{user.businessProfile.whatsapp}</p>
                  </div>
                  <div className="md:col-span-3 space-y-1">
                      <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Key Features</span>
                      <p className="font-medium text-slate-600 leading-relaxed bg-white/50 p-4 rounded-2xl border border-outline-variant/5">
                         {user.businessProfile.key_features}
                      </p>
                  </div>
               </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
             
             {/* Left: Active Traction (Course Tracks) */}
             <div className="lg:col-span-2 space-y-8">
                <div className="flex items-center gap-4 px-2">
                   <div className="w-2 h-8 bg-primary rounded-full" />
                   <h2 className="text-2xl font-black tracking-tight text-on-surface">Active Traction <span className="text-slate-400 font-medium">({activeTracks.length})</span></h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                   {activeTracks.length > 0 ? activeTracks.map((track: any) => (
                      <div key={track.id} className="bg-surface-container-low p-8 rounded-[2.5rem] border border-outline-variant/10 shadow-sm hover:shadow-xl transition-all group">
                         <div className="flex justify-between items-start mb-6">
                            <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center border border-outline-variant/10">
                               <span className="material-symbols-outlined text-primary">school</span>
                            </div>
                            <div className="text-right">
                               <span className="block text-2xl font-black text-on-surface">{track.progress}%</span>
                               <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Complete</span>
                            </div>
                         </div>
                         
                         <h3 className="text-lg font-black text-on-surface mb-6 line-clamp-1">{track.title}</h3>
                         
                         <div className="space-y-4">
                            <div className="h-3 w-full bg-outline-variant/10 rounded-full overflow-hidden">
                               <div 
                                 className="h-full bg-gradient-to-r from-primary to-emerald-400 rounded-full transition-all duration-1000" 
                                 style={{ width: `${track.progress}%` }}
                               />
                            </div>
                            <div className="flex justify-between text-[10px] font-black uppercase tracking-widest text-slate-400">
                               <span>Execution: {track.completedTasks} / {track.totalTasks} Tasks</span>
                               <span>{track.progress === 100 ? "Mastered" : "Active"}</span>
                            </div>
                         </div>
                      </div>
                   )) : (
                      <div className="col-span-full py-20 bg-surface-container-low rounded-[3rem] border border-dashed border-outline-variant/20 flex flex-col items-center justify-center text-center opacity-40">
                         <span className="material-symbols-outlined text-4xl mb-2">pending_actions</span>
                         <p className="font-bold">No active learning tracks detected.</p>
                      </div>
                   )}
                </div>
             </div>

             {/* Right: Execution Ledger (Submissions) */}
             <div className="space-y-8">
                <div className="flex items-center gap-4 px-2">
                   <div className="w-2 h-8 bg-indigo-500 rounded-full" />
                   <h2 className="text-2xl font-black tracking-tight text-on-surface">Execution Ledger</h2>
                </div>

                <div className="space-y-4 bg-surface-container-low p-6 rounded-[3rem] border border-outline-variant/10 shadow-inner max-h-[700px] overflow-y-auto custom-scrollbar">
                   {submissions.length > 0 ? submissions.map((sub: any) => (
                      <button 
                        key={sub.id}
                        onClick={() => setSelectedSubmission(sub)}
                        className="w-full text-left p-6 bg-white rounded-[2rem] border border-outline-variant/10 hover:border-primary/40 hover:shadow-lg transition-all group animate-in fade-in"
                      >
                         <div className="flex justify-between items-start mb-3">
                            <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border ${
                               sub.status === "APPROVED" ? "bg-emerald-55 text-emerald-600 border-emerald-100" :
                               sub.status === "REJECTED" ? "bg-red-50 text-red-600 border-red-100" :
                               "bg-amber-50 text-amber-600 border-amber-100"
                            }`}>
                               {sub.status}
                            </span>
                            <span className="text-[10px] font-medium text-slate-400">
                               {new Date(sub.created_at || sub.createdAt).toLocaleDateString(undefined, { day: '2-digit', month: 'short' })}
                            </span>
                         </div>
                         <h4 className="font-black text-sm text-on-surface line-clamp-1 group-hover:text-primary transition-colors">{sub.taskTitle}</h4>
                         <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wide truncate">{sub.courseTitle}</p>
                      </button>
                   )) : (
                      <div className="py-20 text-center opacity-30">
                         <p className="text-sm font-black italic">The ledger is currently empty.</p>
                      </div>
                   )}
                </div>
             </div>
          </div>
        </div>
      </main>

      {/* Submission Detail Modal (Deferred Content View) */}
      <Dialog 
        isOpen={!!selectedSubmission} 
        onClose={() => setSelectedSubmission(null)} 
        title="Execution Evidence"
      >
        {selectedSubmission && (
          <div className="space-y-8 py-4">
             <div className="flex items-center justify-between p-6 bg-surface-container-low rounded-[2rem] border border-outline-variant/10">
                <div>
                   <h3 className="font-black text-on-surface leading-tight">{selectedSubmission.taskTitle}</h3>
                   <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{selectedSubmission.courseTitle}</span>
                </div>
                <div className={`px-4 py-2 rounded-xl border font-black text-[10px] uppercase tracking-widest ${
                   selectedSubmission.status === "APPROVED" ? "bg-emerald-50 text-emerald-600 border-emerald-100" :
                   selectedSubmission.status === "REJECTED" ? "bg-red-50 text-red-600 border-red-100" :
                   "bg-amber-50 text-amber-600 border-amber-100"
                }`}>
                   {selectedSubmission.status}
                </div>
             </div>

             <div className="space-y-4">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 px-1">Proof of Work</label>
                <div className="p-8 bg-surface-container-lowest rounded-[2.5rem] border border-outline-variant/10 shadow-inner">
                   {selectedSubmission.contentUrl || selectedSubmission.evidenceUrl ? (
                      <div className="space-y-4">
                         {(selectedSubmission.contentUrl || selectedSubmission.evidenceUrl).match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                            <img 
                              src={selectedSubmission.contentUrl || selectedSubmission.evidenceUrl} 
                              alt="Submission Evidence" 
                              className="w-full h-auto rounded-3xl shadow-2xl border border-outline-variant/10"
                            />
                         ) : (
                            <a 
                              href={selectedSubmission.contentUrl || selectedSubmission.evidenceUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="flex items-center gap-4 p-6 bg-white border border-outline-variant/10 rounded-2xl hover:bg-primary/5 hover:border-primary/20 transition-all group"
                            >
                               <span className="material-symbols-outlined text-primary bg-primary/10 w-12 h-12 rounded-xl flex items-center justify-center">open_in_new</span>
                               <div>
                                  <span className="block font-black text-sm group-hover:text-primary">Launch Evidence Protocol</span>
                                  <span className="block text-[10px] font-medium text-slate-500 truncate max-w-[200px]">{selectedSubmission.contentUrl || selectedSubmission.evidenceUrl}</span>
                               </div>
                            </a>
                         )}
                      </div>
                   ) : selectedSubmission.contentBody ? (
                      <div className="prose prose-sm text-on-surface leading-relaxed italic">
                         {selectedSubmission.contentBody}
                      </div>
                   ) : (
                      <div className="text-center py-10 opacity-30 italic font-medium">No tactical evidence attached.</div>
                   )}
                </div>
             </div>

             {selectedSubmission.feedback && (
                <div className="space-y-4">
                   <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 px-1">Command Feedback</label>
                   <div className="p-8 bg-amber-50/50 border border-amber-100 rounded-[2.5rem] italic text-amber-900 leading-relaxed">
                      "{selectedSubmission.feedback}"
                   </div>
                </div>
             )}

             <button 
               onClick={() => setSelectedSubmission(null)}
               className="w-full py-5 bg-surface-container-high text-on-surface font-black text-xs uppercase tracking-widest rounded-[2rem] hover:bg-surface-container-highest transition-all"
             >
                 Close Dossier
             </button>
           </div>
        )}
      </Dialog>

      {/* Tier Management Modal */}
      <Dialog 
        isOpen={isTierModalOpen} 
        onClose={() => setIsTierModalOpen(false)} 
        title="Administer Membership"
      >
        <form onSubmit={handleTierUpdate} className="space-y-6 pt-2">
          <div className="space-y-3 text-center">
            <div className="w-14 h-14 bg-primary/10 text-primary rounded-2xl flex items-center justify-center mx-auto">
              <span className="material-symbols-outlined text-2xl">upgrade</span>
            </div>
            <div>
              <h4 className="font-black text-lg tracking-tight">Assign Subscription Plans</h4>
              <p className="text-xs font-medium text-slate-500">Modify the student's operational privileges immediately.</p>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-2 gap-3 max-h-60 overflow-y-auto p-2">
            {/* Free Plan Option */}
            <button
              type="button"
              onClick={() => setSelectedPlanIds([])}
              className={`p-4 rounded-2xl border-2 transition-all text-left flex items-center gap-3 group ${
                selectedPlanIds.length === 0
                  ? "border-primary bg-primary/5 shadow-md" 
                  : "border-outline-variant/10 hover:border-primary/30"
              }`}
            >
              <div className="flex-1 min-w-0">
                <span className={`block text-[10px] font-black tracking-widest uppercase truncate ${selectedPlanIds.length === 0 ? "text-primary" : "text-slate-500"}`}>
                  FREE
                </span>
                <span className="block text-xs font-bold text-slate-400 mt-0.5 truncate">
                  Tier 0
                </span>
              </div>
              <span className={`material-symbols-outlined transition-colors ${selectedPlanIds.length === 0 ? "text-primary" : "text-transparent"}`}>
                check_circle
              </span>
            </button>

            {plans.map((plan: any) => (
              <button
                key={plan.id}
                type="button"
                onClick={() => {
                  setSelectedPlanIds(prev => 
                    prev.includes(Number(plan.id)) ? prev.filter(id => id !== Number(plan.id)) : [...prev, Number(plan.id)]
                  );
                }}
                className={`p-4 rounded-2xl border-2 transition-all text-left flex items-center gap-3 group ${
                  selectedPlanIds.includes(Number(plan.id))
                    ? "border-primary bg-primary/5 shadow-md" 
                    : "border-outline-variant/10 hover:border-primary/30"
                }`}
              >
                <div className="flex-1 min-w-0">
                  <span className={`block text-[10px] font-black tracking-widest uppercase truncate ${selectedPlanIds.includes(Number(plan.id)) ? "text-primary" : "text-slate-500"}`}>
                    {plan.name}
                  </span>
                  <span className="block text-xs font-bold text-slate-400 mt-0.5 truncate">
                    Tier {plan.tier_level}
                  </span>
                </div>
                <span className={`material-symbols-outlined transition-colors ${selectedPlanIds.includes(Number(plan.id)) ? "text-primary" : "text-transparent"}`}>
                  check_circle
                </span>
              </button>
            ))}
          </div>

          <button 
            type="submit" 
            disabled={isSubmitting}
            className="w-full py-4 bg-primary text-surface rounded-2xl font-black text-xs uppercase tracking-widest hover:translate-y-[-2px] transition-all shadow-xl shadow-primary/20 disabled:opacity-50 mt-4"
          >
            {isSubmitting ? "Processing..." : "Finalize Transformation"}
          </button>
        </form>
      </Dialog>

      {/* Bonus Creations Management Modal */}
      <Dialog 
        isOpen={isExtraModalOpen} 
        onClose={() => setIsExtraModalOpen(false)} 
        title="Award Bonus Creations"
      >
        <form onSubmit={handleExtraCreationsUpdate} className="space-y-8 pt-4">
          <div className="space-y-4">
            <p className="text-sm font-bold text-slate-500">
               Award extra bonus creations (strategies or posters) to this student. This value directly increases their allowed creation quota.
            </p>
            <div className="flex flex-col gap-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 px-1">Bonus Creation Quota</label>
              <input 
                type="number"
                min="0"
                max="100"
                value={extraCreationsInput}
                onChange={(e) => setExtraCreationsInput(Math.max(0, parseInt(e.target.value, 10) || 0))}
                className="w-full px-6 py-4 bg-surface-container-low border border-outline-variant/10 rounded-2xl focus:border-primary/30 outline-none transition-all font-black text-sm text-center"
                placeholder="e.g. 5"
              />
            </div>
          </div>
          <button 
            type="submit" 
            disabled={isSubmittingExtra}
            className="w-full py-5 bg-indigo-500 text-white rounded-[2rem] font-black text-xs uppercase tracking-widest hover:translate-y-[-4px] transition-all shadow-xl shadow-indigo-500/20 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isSubmittingExtra ? (
              <span className="material-symbols-outlined animate-spin text-sm">sync</span>
            ) : (
              <span className="material-symbols-outlined text-sm">workspace_premium</span>
            )}
            {isSubmittingExtra ? "Saving..." : "Award Extra Creations"}
          </button>
        </form>
      </Dialog>
    </div>
  );
}
